import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { format } from "date-fns";
import {
  areFiltersSelected,
  LoadingIndicator,
  NoResultsIndicatorAnimation,
  useDeviceFilterValue
} from "elements";
import { ConnectionStatusIcon } from "elements";
import {
  useDeviceUsersListQuery,
  useTenantAccessorsQuery
} from "generated/graphql";
import { infiniteNestLink, Link, useHistory } from "polyrouter";
import { needs, useGuard } from "rbac";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "utilities/i18n";
import { useSelector } from "react-redux";
import VisibilitySensor from "react-visibility-sensor";
import {
  ConnectionStatusCell,
  ConnectionStatusHeader
} from "routes/Manage/Inventory/List/TableView";
import { State } from "store";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import {
  ProtectedDropdownButton,
  ProtectedLink,
  ProtectedRoute
} from "TsRoleGuards";
import { handleNullFields } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import InviteMemberModal from "routes/Admin/Accounts/Members/InviteMemberModal";
import RemoveDeviceUserModal from "../Delete/RemoveDeviceUserModal";
import MigrateDeviceUsersModal from "../Update/MigrateDeviceUsersModal";
import {
  DeviceUserInfo,
  getDeviceConnections,
  getDesktopInfo,
  getMobileInfo
} from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { useDeviceUserCount } from "routes/Manage/DeviceUsers/Shared/utils/useDeviceUserCount";
import { RelevantTenantInfo } from "routes/Admin/Accounts/Members/Helpers";
import {
  AddUsersModal,
  CreateGroupModal,
  RemoveUsersModal
} from "routes/Manage/DeviceUsers/Shared/Modals";
import { getLastDetectedDate } from "utilities/misc";

export type DeviceUserRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
  endCursor: string;
  index: number;
  userListLength: number;
  hasNextPage?: boolean;
  setCursor: (arg: string) => void;
} & Pick<
  DeviceUserInfo,
  "user_id" | "name" | "lensDesktopInfo" | "lensMobileInfo" | "connectedDevices"
>;

export const DeviceUsersList: React.FC<{
  tenant: RelevantTenantInfo;
}> = ({ tenant }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const userCanEdit = useGuard(needs("grant:role"));
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [filterState, setFilter, resetFilters] = useDeviceFilterValue();
  const [deviceUserList, setDeviceUserList] = useState<Array<DeviceUserInfo>>(
    []
  );
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [usersSearchTerm, setUsersSearchTerm] = useState<string | undefined>(
    undefined
  );
  const [dispTid, setDispTid] = useState<string | undefined>(undefined);
  const [hasDeviceUsers, setHasDeviceUsers] = useState(false);

  const { deviceUserCount } = useDeviceUserCount(tenant.id);

  const {
    data: deviceUsers,
    fetching: fetchingDeviceUsers,
    stale,
    executor
  } = useDeviceUsersListQuery({
    variables: {
      params: {
        grants: [
          {
            resourceId: tenant.id,
            roles: ["device_user"]
          }
        ],
        sortBy: "name",
        searchTerm: usersSearchTerm
      },
      first: 50,
      after: cursor
    }
  });

  //Get all users with access to this tenant
  const { data: rolesData, fetching: rolesFetching } = useTenantAccessorsQuery({
    variables: {
      params: {
        resourceId: tenant.id,
        resourceType: "Tenant"
      }
    }
  });

  useEffect(() => {
    // Create an array of objects with device user details
    let users = new Array<DeviceUserInfo>();
    deviceUsers?.deviceUsersResult?.deviceUsersInfo.forEach(user => {
      const userGrants =
        rolesData?.accessors.filter(
          accessor => accessor.id === user.node.user_id
        ) || [];

      if (userGrants?.length > 0) {
        userGrants[0]?.grants.forEach(grant => {
          if (grant.resourceId === tenant.id) {
            const roles = grant.roles.map(role => role.name);
            for (const role of roles) {
              if (role === "device_user") {
                const lensDesktopInfo = getDesktopInfo(
                  user.node.connections,
                  tenant.id
                );
                const lensMobileInfo = getMobileInfo(
                  user.node.connections,
                  tenant.id
                );
                const connectedDevices = getDeviceConnections(
                  lensDesktopInfo?.connections
                );
                users.push({
                  ...user.node,
                  connectedDevices,
                  lensDesktopInfo,
                  lensMobileInfo
                });
                setHasDeviceUsers(true);
                break;
              }
            }
          }
        });
      }
    });

    if (rolesData) {
      setDeviceUserList(users);
    }
  }, [tenant, rolesData, deviceUsers]);

  const loading = fetchingDeviceUsers || rolesFetching || stale;

  useEffect(() => {
    setSelectedItems([]);
    if (!dispTid) {
      setDispTid(tenant.id);
      return;
    }
    if (tenant.id !== dispTid) {
      setCursor(undefined);
    }
  }, [tenant, setCursor, dispTid, history]);

  const endCursor = deviceUsers?.deviceUsersResult?.pageInfo.endCursor;

  const toggleItem = toggleItemWithSet(
    deviceUserList.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const selectedDeviceUsers = selectedItems.reduce<
    Array<{ name: string; id: string }>
  >((deviceUsersList, id) => {
    const deviceUserObj = deviceUserList.find(user => user.user_id === id);
    return deviceUserObj
      ? [...deviceUsersList, { name: deviceUserObj.name, id }]
      : deviceUsersList;
  }, []);

  const refresh = () => {
    setSelectedItems([]);
    executor({
      requestPolicy: "network-only"
    });
    history.push("/manage/device-users/users");
  };

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.DEVICE_USERS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_USER_NAME"),
    [COLUMNS.PRIMARY_DEVICE]: t("PRIMARY_DEVICE"),
    [COLUMNS.APP_VERSION]: t("CLIENT_APP_VERSION"),
    [COLUMNS.DEVICE_COUNT]: t("DEVICE_COUNT"),
    [COLUMNS.LAST_DETECTED]: t("LAST_DETECTED"),
    [COLUMNS.CLIENT]: t("CLIENT")
  };

  const getTableHeaders = (column: string, title: string) => {
    return <TableHeaderItem fieldName={column} title={title} key={column} />;
  };
  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);
  // there are two additional columns besides those user choose to display (checkbox to select rows and user online status)
  const columnCount = shown.length + 2;
  const usersSelected = selectedItems.length > 0;

  const { Content } = Layout;
  return (
    <Content className="small-sidebar-nav-padding device-user-inventory">
      {!loading && !hasDeviceUsers ? (
        <NoResultsIndicatorAnimation title={t("NO_DEVICE_USERS")}>
          <ProtectedLink
            nest
            to={{ pathname: "/add", search: "device-user=true" }}
            data-testid="add-device-user"
            className="btn btn-primary"
            requirement="grant:role"
          >
            {t("ADD_NEW")}
          </ProtectedLink>
        </NoResultsIndicatorAnimation>
      ) : (
        <>
          <div className="table-heading">
            <span className="metric">{deviceUserCount}</span>
            <span className="title ml-5">{t("DEVICE_USERS")}</span>
          </div>
          <div className="grouping mb-15 table-actions">
            <ProtectedLink
              nest
              to={{ pathname: "/add", search: "device-user=true" }}
              data-testid="add-device-user"
              className="btn btn-primary"
              requirement="grant:role"
            >
              {t("ADD_NEW")}
            </ProtectedLink>
            <ProtectedDropdownButton
              disabled={!usersSelected}
              className={`btn btn-secondary${usersSelected ? "" : " disabled"}`}
              requirement="update:usergroup"
              title={t("GROUP")}
            >
              <Link to={infiniteNestLink("/group/create")}>
                <span className=".menu.router li">{t("CREATE_NEW_GROUP")}</span>
              </Link>
              <Link to={infiniteNestLink("/group/add")}>
                <span className=".menu.router li">
                  {t("ADD_TO_EXISTING_GROUP")}
                </span>
              </Link>
              <Link to={infiniteNestLink("/group/remove")}>
                <span className=".menu.router li">
                  {t("REMOVE_FROM_GROUP")}
                </span>
              </Link>
            </ProtectedDropdownButton>
            <ProtectedLink
              disabled={!usersSelected}
              to={infiniteNestLink("/migrate")}
              className={`btn btn-secondary ${usersSelected ? "" : "disabled"}`}
              data-testid="migrate-device-user"
              requirement="grant:role"
            >
              {t("MIGRATE")}
            </ProtectedLink>
            <ProtectedLink
              to={infiniteNestLink("/remove")}
              disabled={!usersSelected}
              className={`btn ${usersSelected ? "" : "disabled"}`}
              data-testid="remove-device-user"
              requirement="grant:role"
            >
              {t("DELETE")}
            </ProtectedLink>
            <TableSearchEdit
              search={{
                onSearch: (searchTerm: string | undefined) => {
                  if (searchTerm !== usersSearchTerm) {
                    setCursor(undefined);
                    setUsersSearchTerm(searchTerm);
                  }
                }
              }}
              edit={{
                tableName: TABLE_NAME,
                shownColumns: shown,
                hiddenColumns: hidden
              }}
            />
          </div>
          <div className="table-container">
            <table className="table custom-table table-filterable">
              <TableBulkItemHeader
                showChecks={userCanEdit}
                checked={itemChecked(HEADER_ID)}
                onCheck={() =>
                  toggleAll(
                    deviceUserList.map(user => user.user_id),
                    selectedItems,
                    setSelectedItems
                  )
                }
                onClearFilters={resetFilters}
                showClearFilters={areFiltersSelected(filterState)}
              >
                <ConnectionStatusHeader
                  fieldName="status"
                  filterState={filterState}
                  setFilter={setFilter}
                />
                {generateTableHeaders(shown, getTableHeaders)}
              </TableBulkItemHeader>
              <TableBody
                loading={false}
                cols={columnCount}
                accommodateScrollbar
              >
                {deviceUserList.map((user, i) => {
                  return (
                    <DeviceUserRow
                      {...user}
                      key={user.user_id}
                      hasNextPage={
                        deviceUsers?.deviceUsersResult?.pageInfo.hasNextPage
                      }
                      index={i}
                      userListLength={deviceUserList.length}
                      endCursor={endCursor}
                      onCheck={() => toggleItem(user.user_id)}
                      checked={itemChecked(user.user_id)}
                      showChecks={userCanEdit}
                      shownColumns={shown.map(column => column.key)}
                      setCursor={(cursor: string) => setCursor(cursor)}
                    />
                  );
                })}
              </TableBody>
            </table>
            {loading ? (
              <LoadingIndicator
                key="loading"
                className={deviceUserList.length ? "inline text-lg" : ""}
              />
            ) : null}
          </div>
        </>
      )}
      <ProtectedRoute nest path="/add" requirement="grant:role">
        <InviteMemberModal tenant={tenant} />
      </ProtectedRoute>
      <ProtectedRoute nest path="/group/add" requirement="update:usergroup">
        <AddUsersModal
          tenantId={tenant.id}
          deviceUsers={selectedDeviceUsers}
          urlEndPath="/group/add"
          showLinkOnSuccess={true}
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/group/create" requirement="update:usergroup">
        <CreateGroupModal
          tenantId={tenant.id}
          deviceUsers={selectedDeviceUsers}
          urlEndPath="/group/create"
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/group/remove" requirement="update:usergroup">
        <RemoveUsersModal
          tenantId={tenant.id}
          deviceUsers={selectedDeviceUsers}
          urlEndPath="/group/remove"
          showLinkOnSuccess={true}
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/migrate" requirement="grant:role">
        <MigrateDeviceUsersModal
          refresh={refresh}
          deviceUsers={selectedDeviceUsers}
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/remove" requirement="grant:role">
        <RemoveDeviceUserModal
          refresh={refresh}
          deviceUsers={selectedDeviceUsers}
        />
      </ProtectedRoute>
    </Content>
  );
};

export const DeviceUserRow: React.FC<DeviceUserRowProps> = ({
  name,
  user_id,
  lensDesktopInfo,
  lensMobileInfo,
  connectedDevices,
  index,
  userListLength,
  endCursor,
  hasNextPage,
  checked,
  showChecks,
  onCheck,
  shownColumns,
  setCursor
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const { COLUMNS } = TABLES.DEVICE_USERS;
  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      setVisible(isVisible);
      if (index === userListLength - 1 && hasNextPage) {
        setCursor(endCursor);
      }
    }
  };

  const [filterState] = useDeviceFilterValue();

  const connected = useMemo(
    () => lensDesktopInfo?.connected || lensMobileInfo?.connected,
    [lensMobileInfo?.connected, lensDesktopInfo?.connected]
  );

  if (
    filterState &&
    ((filterState.status === "online" && !connected) ||
      (filterState.status === "offline" && connected))
  ) {
    return <></>;
  }

  const connectionsLength = connectedDevices?.length;

  const primaryDevice =
    connectionsLength && connectedDevices
      ? connectedDevices[connectionsLength - 1]?.name
      : null;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link
            nest
            to={{
              pathname: "/details",
              search: `user_id=${user_id}`
            }}
          >
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.PRIMARY_DEVICE:
        content = handleNullFields(primaryDevice);
        break;
      case COLUMNS.APP_VERSION:
        if (lensMobileInfo && lensDesktopInfo)
          content = handleNullFields(
            `${lensMobileInfo.softwareVersion} / ${lensDesktopInfo.softwareVersion}`
          );
        else if (lensMobileInfo)
          content = handleNullFields(lensMobileInfo.softwareVersion);
        else content = handleNullFields(lensDesktopInfo?.softwareVersion);
        break;
      case COLUMNS.DEVICE_COUNT:
        content = connectionsLength || 0;
        break;
      case COLUMNS.LAST_DETECTED:
        const lastDetected = getLastDetectedDate(
          lensDesktopInfo?.lastDetected,
          lensMobileInfo?.lastDetected
        );
        content = lastDetected
          ? format(lastDetected, "M/d/yyyy")
          : handleNullFields(null);
        break;
      case COLUMNS.CLIENT:
        if (lensMobileInfo && lensDesktopInfo)
          content = handleNullFields(t("MOBILE_AND_DESKTOP"));
        else if (lensMobileInfo) content = handleNullFields(t("MOBILE"));
        else if (lensDesktopInfo) content = handleNullFields(t("DESKTOP"));
        else content = handleNullFields(null);
        break;
    }

    return <td key={column}>{content}</td>;
  };
  return (
    <VisibilitySensor
      offset={{ bottom: -1000 }}
      active={!visible}
      onChange={onChange}
    >
      <TableBulkItemRow
        checked={checked}
        onCheck={() => onCheck(user_id)}
        showChecks={showChecks}
      >
        <ConnectionStatusCell>
          <ConnectionStatusIcon connected={!!connected} className="m-0" />
        </ConnectionStatusCell>
        {shownColumns.map(getTableContent)}
      </TableBulkItemRow>
    </VisibilitySensor>
  );
};
